import React, { useState, useEffect, useRef } from 'react';
import { Document, Packer, Paragraph, HeadingLevel, TextRun, List, AlignmentType } from 'docx';
import { saveAs } from 'file-saver';

const CustomForm = () => {
  // 初始表单数据
  const initialFormData = {
    material: '',
    length: '',
    color: '',
    budget: '',
    designPreference: '',
    specialRequest: '',
    name: '',
    phone: ''
  };

  // 状态管理
  const [formData, setFormData] = useState(initialFormData);
  const [showSummary, setShowSummary] = useState(false);
  
  // 创建ref用于获取PDF内容
  const pdfRef = useRef(null);

  // 从localStorage加载数据
  useEffect(() => {
    const savedData = localStorage.getItem('customFormData');
    if (savedData) {
      setFormData(JSON.parse(savedData));
    }
  }, []);

  // 保存数据到localStorage
  useEffect(() => {
    localStorage.setItem('customFormData', JSON.stringify(formData));
  }, [formData]);

  // 处理输入变化
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // 处理提交
  const handleSubmit = (e) => {
    e.preventDefault();
    setShowSummary(true);
  };

  // 重置表单
  const resetForm = () => {
    setFormData(initialFormData);
    localStorage.removeItem('customFormData');
    setShowSummary(false);
  };

  // 生成Word文件，支持可复制文字和中文
  const generateWord = () => {
    try {
      // 创建Word文档
      const doc = new Document({
        sections: [
          {
            properties: {},
            children: [
              // 标题
              new Paragraph({
                text: '手链定制需求摘要',
                heading: HeadingLevel.HEADING_1,
                alignment: AlignmentType.CENTER,
                spacing: {
                  after: 400,
                },
              }),
              
              // 基本信息
              new Paragraph({
                text: '基本信息',
                heading: HeadingLevel.HEADING_2,
                spacing: {
                  after: 200,
                },
              }),
              
              // 基本信息
              new Paragraph({
                children: [
                  new TextRun({ text: '联系人：', bold: true }),
                  new TextRun({ text: formData.name || '未填写' }),
                ],
                spacing: {
                  after: 150,
                },
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: '联系电话：', bold: true }),
                  new TextRun({ text: formData.phone || '未填写' }),
                ],
                spacing: {
                  after: 200,
                },
              }),
              
              // 定制需求
              new Paragraph({
                text: '定制需求',
                heading: HeadingLevel.HEADING_2,
                spacing: {
                  before: 300,
                  after: 200,
                },
              }),
              
              // 定制需求
              new Paragraph({
                children: [
                  new TextRun({ text: '材质：', bold: true }),
                  new TextRun({ text: formData.material || '未填写' }),
                ],
                spacing: {
                  after: 150,
                },
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: '长度：', bold: true }),
                  new TextRun({ text: formData.length || '未填写' }),
                ],
                spacing: {
                  after: 150,
                },
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: '颜色：', bold: true }),
                  new TextRun({ text: formData.color || '未填写' }),
                ],
                spacing: {
                  after: 150,
                },
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: '预算：', bold: true }),
                  new TextRun({ text: formData.budget || '未填写' }),
                ],
                spacing: {
                  after: 200,
                },
              }),
              
              // 设计偏好与特殊要求
              new Paragraph({
                text: '设计偏好与特殊要求',
                heading: HeadingLevel.HEADING_2,
                spacing: {
                  before: 300,
                  after: 200,
                },
              }),
              
              // 设计偏好
              new Paragraph({
                children: [
                  new TextRun({ text: '设计偏好：', bold: true }),
                ],
                spacing: {
                  after: 100,
                },
              }),
              new Paragraph({
                text: formData.designPreference || '未填写',
                spacing: {
                  after: 200,
                },
              }),
              
              // 特殊要求
              new Paragraph({
                children: [
                  new TextRun({ text: '特殊要求：', bold: true }),
                ],
                spacing: {
                  after: 100,
                },
              }),
              new Paragraph({
                text: formData.specialRequest || '未填写',
                spacing: {
                  after: 300,
                },
              }),
              
              // 页脚
              new Paragraph({
                alignment: AlignmentType.CENTER,
                spacing: {
                  before: 300,
                },
                children: [
                  new TextRun({ text: '逸流珠宝 - 定制需求摘要', size: 20 }),
                ],
              }),
            ],
          },
        ],
      });
      
      // 生成Word文件并下载
      Packer.toBlob(doc).then((blob) => {
        saveAs(blob, '手链定制需求摘要.docx');
      }).catch((error) => {
        console.error('Word生成失败:', error);
      });
    } catch (error) {
      console.error('Word生成失败:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 py-8">
      <div className="container mx-auto px-4">
        <h1 className="text-3xl font-bold text-center mb-8 text-emerald-800">
          手链定制表单
        </h1>
        
        {/* 表单区域 */}
        <div className="bg-white rounded-xl shadow-lg p-6 md:p-8 mb-8 border border-green-100">
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* 基本信息 */}
              <div>
                <h2 className="text-xl font-semibold mb-4 text-emerald-700">
                  基本信息
                </h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-gray-700 mb-1">
                      联系人
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 rounded-lg border border-green-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 mb-1">
                      联系电话
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 rounded-lg border border-green-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
              
              {/* 定制需求 */}
              <div>
                <h2 className="text-xl font-semibold mb-4 text-emerald-700">
                  定制需求
                </h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-gray-700 mb-1">
                      材质
                    </label>
                    <input
                      type="text"
                      name="material"
                      value={formData.material}
                      onChange={handleInputChange}
                      placeholder="如：紫水晶、玫瑰石英等"
                      className="w-full px-4 py-2 rounded-lg border border-green-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 mb-1">
                      长度
                    </label>
                    <input
                      type="text"
                      name="length"
                      value={formData.length}
                      onChange={handleInputChange}
                      placeholder="如：18cm"
                      className="w-full px-4 py-2 rounded-lg border border-green-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 mb-1">
                      颜色
                    </label>
                    <input
                      type="text"
                      name="color"
                      value={formData.color}
                      onChange={handleInputChange}
                      placeholder="如：紫色、粉色、蓝色等"
                      className="w-full px-4 py-2 rounded-lg border border-green-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 mb-1">
                      预算
                    </label>
                    <input
                      type="text"
                      name="budget"
                      value={formData.budget}
                      onChange={handleInputChange}
                      placeholder="如：¥300-500"
                      className="w-full px-4 py-2 rounded-lg border border-green-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
              
              {/* 设计偏好 */}
              <div className="md:col-span-2">
                <h2 className="text-xl font-semibold mb-4 text-emerald-700">
                  设计偏好与特殊要求
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-gray-700 mb-1">
                      设计偏好
                    </label>
                    <textarea
                      name="designPreference"
                      value={formData.designPreference}
                      onChange={handleInputChange}
                      placeholder="如：简约风格、多层设计、加入特定元素等"
                      rows={4}
                      className="w-full px-4 py-2 rounded-lg border border-green-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    ></textarea>
                  </div>
                  <div>
                    <label className="block text-gray-700 mb-1">
                      特殊要求
                    </label>
                    <textarea
                      name="specialRequest"
                      value={formData.specialRequest}
                      onChange={handleInputChange}
                      placeholder="如：特定尺寸、刻字需求、加急制作等"
                      rows={4}
                      className="w-full px-4 py-2 rounded-lg border border-green-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    ></textarea>
                  </div>
                </div>
              </div>
            </div>
            
            {/* 提交按钮 */}
            <div className="flex justify-center gap-4 mt-8">
              <button
                type="submit"
                className="px-6 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-lg hover:from-emerald-600 hover:to-teal-600 transition-colors duration-200 font-medium"
              >
                提交需求
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="px-6 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors duration-200 font-medium"
              >
                重置表单
              </button>
            </div>
          </form>
        </div>

        {/* 需求摘要 */}
        {showSummary && (
          <div ref={pdfRef} className="bg-white rounded-xl shadow-lg p-6 md:p-8 border border-green-100">
            <h2 className="text-xl font-semibold mb-6 text-emerald-700 text-center">
              定制需求摘要
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold mb-4 text-emerald-600">
                  基本信息
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">联系人：</span>
                    <span className="font-medium">{formData.name || '未填写'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">联系电话：</span>
                    <span className="font-medium">{formData.phone || '未填写'}</span>
                  </div>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-4 text-emerald-600">
                  定制需求
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">材质：</span>
                    <span className="font-medium">{formData.material || '未填写'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">长度：</span>
                    <span className="font-medium">{formData.length || '未填写'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">颜色：</span>
                    <span className="font-medium">{formData.color || '未填写'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">预算：</span>
                    <span className="font-medium">{formData.budget || '未填写'}</span>
                  </div>
                </div>
              </div>
              <div className="md:col-span-2">
                <h3 className="text-lg font-semibold mb-4 text-emerald-600">
                  设计偏好与特殊要求
                </h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="text-gray-600 mb-1">设计偏好：</h4>
                    <p className="font-medium">{formData.designPreference || '未填写'}</p>
                  </div>
                  <div>
                    <h4 className="text-gray-600 mb-1">特殊要求：</h4>
                    <p className="font-medium">{formData.specialRequest || '未填写'}</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* 导出文件按钮 */}
            <div className="flex justify-center mt-8">
              <button
                onClick={generateWord}
                className="px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-lg hover:from-blue-600 hover:to-indigo-600 transition-colors duration-200 font-medium"
              >
                导出为Word
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomForm;